// src/app/services/mock-data.service.ts
import { Injectable } from "@angular/core";
import { faker } from "@faker-js/faker";
import Fuse from "fuse.js";

export const fakerDictionary = [
  // General
  { keyword: "name", fakerMethod: () => faker.person.fullName() },
  { keyword: "email", fakerMethod: () => faker.internet.email() },
  { keyword: "phone", fakerMethod: () => faker.phone.number() },
  { keyword: "address", fakerMethod: () => faker.location.streetAddress() },
  { keyword: "city", fakerMethod: () => faker.location.city() },
  { keyword: "country", fakerMethod: () => faker.location.country() },
  { keyword: "date", fakerMethod: () => faker.date.past().toISOString() },

  // Letter of Credit (LC)
  {
    keyword: "lc_number",
    fakerMethod: () =>
      `LC-${faker.string.alphanumeric({ length: 10, casing: "upper" })}`,
  },
  {
    keyword: "lc_amount",
    fakerMethod: () =>
      faker.finance.amount({ min: 10000, max: 1000000, dec: 2 }),
  },
  {
    keyword: "lc_iso_curr_code",
    fakerMethod: () => faker.finance.currencyCode(),
  },
  { keyword: "applicant_party", fakerMethod: () => faker.company.name() },
  { keyword: "beneficiary_party", fakerMethod: () => faker.company.name() },
  { keyword: "issuing_bank", fakerMethod: () => faker.company.name() },
  {
    keyword: "product_status_code",
    fakerMethod: () =>
      faker.helpers.arrayElement([
        "Drafts",
        "Issued",
        "Settled",
        "Partially Settled",
      ]),
  },

  // Loans
  {
    keyword: "loan_amount",
    fakerMethod: () =>
      faker.finance.amount({ min: 50000, max: 5000000, dec: 2 }),
  },
  {
    keyword: "interest_rate",
    fakerMethod: () => Number((Math.random() * (12 - 2) + 2).toFixed(2)),
  },

  {
    keyword: "loan_type",
    fakerMethod: () =>
      faker.helpers.arrayElement(["Term Loan", "Overdraft", "Working Capital"]),
  },
  {
    keyword: "loan_status",
    fakerMethod: () =>
      faker.helpers.arrayElement(["Pending", "Approved", "Rejected"]),
  },
  {
    keyword: "type",
    fakerMethod: () =>
      faker.helpers.arrayElement(["Type A", "Type B", "Type C"]),
  },
  // Party
  {
    keyword: "party_code",
    fakerMethod: () =>
      `PTY-${faker.string.alphanumeric({ length: 8, casing: "upper" })}`,
  },
  {
    keyword: "party_type",
    fakerMethod: () =>
      faker.helpers.arrayElement([
        "Customer",
        "Bank",
        "Applicant",
        "Beneficiary",
      ]),
  },
  { keyword: "party_name", fakerMethod: () => faker.company.name() },
  {
    keyword: "country_code",
    fakerMethod: () => faker.location.countryCode("alpha-2"),
  },
  { keyword: "currency", fakerMethod: () => faker.finance.currencyCode() },

  // Common financial fields
  {
    keyword: "reference_id",
    fakerMethod: () =>
      `REF-${faker.string.alphanumeric({ length: 12, casing: "upper" })}`,
  },
  {
    keyword: "reference",
    fakerMethod: () =>
      `REF-${faker.string.alphanumeric({ length: 6, casing: "upper" })}`,
  },

  {
    keyword: "status_code",
    fakerMethod: () =>
      faker.helpers.arrayElement(["NEW", "PENDING", "APPROVED", "REJECTED"]),
  },
  {
    keyword: "financing_type",
    fakerMethod: () =>
      faker.helpers.arrayElement(["Factoring", "Forfaiting", "Term Loan"]),
  },
  {
    keyword: "beneficiary_country_code",
    fakerMethod: () => faker.location.countryCode("alpha-2"),
  },
  {
    keyword: "address",
    fakerMethod: () => faker.location.streetAddress(false),
  },
];

export const fuse = new Fuse(fakerDictionary, {
  keys: ["keyword"],
  threshold: 0.5, // adjust based on matching tolerance
});

export function generateMockRecord(fields: any[]): any {
  const record: any = {};
  fields.forEach((field) => {
    const key = field.name.toUpperCase();
    const type = field.type;

    record[key] = getFakeValue(type, field.displayName);
  });

  record["VERSION_ID"] = 1;
  record["STAGE_CODE"] = "INITIATION";
  record["TYPE_CODE"] = "FG_TFN_FINANCE_REQ";
  record["SUB_TYPE_CODE"] = "FG_TFN_FINANCE_REQUEST";
  record["IDENTIFIER_KEY"] = JSON.stringify({
    VERSION_ID: 1,
    REFERENCE_ID: record["REFERENCE_ID"],
    ID: record["ID"] || faker.string.uuid(),
  });

  return record;
}

export function generateMockList(
  fields: any[],
  count: number = 14,
  code: string
) {
  const entityData = Array.from({ length: count }, () =>
    generateMockRecord(fields)
  );
  return {
    totalCount: count,
    entityData,
    errors: [],
    facetData: null,
    userPermissions: [],
  };
}
export function generateMockItem(
  fields: any[],
  count: number = 1,
  code: string
) {
  const entityData = Array.from({ length: count }, () =>
    generateMockRecord(fields)
  );
  return entityData;
}

export function getFakeValue(type: string, name: string): any {
  const nameLower = name?.toLowerCase() || "";

  const result = fuse.search(nameLower);
  if (result.length > 0) {
    return result[0].item.fakerMethod();
  }

  // fallback logic
  if (type === "decimal" || type == "integer") {
    return faker.number.int({ min: 1, max: 100 });
  }

  if (type === "datetime" || type === "date") {
    return faker.date.recent().toISOString();
  }

  if (type === "boolean") {
    return faker.datatype.boolean();
  }
  if (type === "text" || type === "textarea") {
    return faker.person.fullName();
  }
  return "";
}
export function analyticalMock(request: any): any[] {
  const dimensionValueMap: Record<string, string[]> = {
    STATUS_CODE: ["NEW", "PENDING", "APPROVED", "REJECTED"],
    PRODUCT_STATUS_CODE: ["Drafts", "Issued", "Partially Settled", "Settled"],
    BENEFICIARY_COUNTRY_CODE: ["India", "United States", "US", "Germany", "Japan"],
    CURRENCY: ["USD", "EUR", "JPY", "INR"],
    FINANCING_TYPE: ["Pre-shipment", "Post-shipment", "Working Capital"],
  };

  const result: any[] = [];
  const measureFields = request?.measureFields || [];
  const dimensionFields = request?.dimensionFields || [];
  const range = request?.range ? JSON.parse(request.range) : [0, 4];

  const entriesCount = Math.min(range[1] - range[0], 10);

  // const dateMeasure = dimensionFields.find(
  //   (m) => ["date", "datetime"].includes(m.type?.toLowerCase())
  // );

  function formatDateToYYYYMMDD(date: Date): string {
  return date.toISOString().split('T')[0];
}
const dateMeasure = dimensionFields.find((dim) =>
  dim?.field?.toLowerCase().includes("date") ||
  dim?.field?.toLowerCase().includes("time")
);
  const isTimeSeries = dateMeasure && dimensionFields.length > 1;

  for (let i = 0; i < entriesCount; i++) {
    const entry: any = {};

    // Use current dimension field value for grouping (first one only)
    for (const dimension of dimensionFields) {
      const alias = dimension.field;
      const lower = (dimension.field || "").toLowerCase();

      if (lower.includes("country")) {
        entry[alias] = faker.location.country();
      } else if (lower.includes("currency")) {
        entry[alias] = faker.finance.currencyCode();
      } else if (lower.includes("status")) {
        entry[alias] = faker.helpers.arrayElement(["New", "Pending", "Approved", "Rejected"]);
      } else if (lower.includes("name")) {
        entry[alias] = faker.person.fullName();
      } else if (lower.includes("type")) {
        entry[alias] = faker.commerce.product();
      }else if (lower.includes("date")) {
        entry[alias] = formatDateToYYYYMMDD(faker.date.past({ years: 1 }));
      } else if (lower.includes("time")) {
        entry[alias] =formatDateToYYYYMMDD(faker.date.past({ years: 1 }));
      }else {
        entry[alias] = faker.word.words(2);
      }
    }


function toCamelCase(str: string): string {
  return str
    .toLowerCase()
    .split(/[_\s]+/)
    .map((word, index) =>
      index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join("");
}

    
      // Standard mock generation if not time series
      for (const m of measureFields) {
        const alias = m.alise;
        const isCount = (m.aggregation || "").toLowerCase().includes("count");
        const isDate = ["date", "datetime"].includes(m.type?.toLowerCase());

        entry[alias] = isDate
          ? faker.date.recent().toISOString()
          : isCount
          ? Math.floor(Math.random() * 100 + 1)
          : Math.floor(Math.random() * 1_000_000 + 10_000);
      }


    // Convert keys to upper case
    const upperCasedEntry: any = {};
    for (const key in entry) {
      if (entry.hasOwnProperty(key)) {
        upperCasedEntry[key.toUpperCase()] = entry[key];
      }
    }

    result.push(upperCasedEntry);
  }
  ////expandTimeSeries(result);
if(isTimeSeries) return  expandTimeSeries(result);

else
  return result;
}
function expandTimeSeries(data: any[]): any[] {
  if (!data || data.length === 0) return [];

  // Identify the date field
  const dateFieldKeys = Object.keys(data[0]).filter(k =>
    k.toLowerCase().includes('date')
  );
  const dateKey = dateFieldKeys[0]; // Assume one date field

  // Identify dimensions (strings but not the date)
  const dimensionKeys = Object.keys(data[0]).filter(
    k => typeof data[0][k] === 'string' && !k.toLowerCase().includes('date')
  );

  // Identify measure fields (numbers)
  const measureKeys = Object.keys(data[0]).filter(
    k => typeof data[0][k] === 'number'
  );

  // Gather all unique dates, sorted
  const allDates = Array.from(
    new Set(data.map(d => d[dateKey]).filter(Boolean))
  ).sort();

  // Group by dimension values
  const grouped = new Map<string, any[]>();

  for (const row of data) {
    const dimKey = dimensionKeys.map(k => row[k]).join('|');
    if (!grouped.has(dimKey)) grouped.set(dimKey, []);
    grouped.get(dimKey)?.push(row);
  }

  const result: any[] = [];

  for (const [dimKey, rows] of grouped.entries()) {
    const dimParts = dimKey.split('|');
    const dimensionValues: Record<string, string> = {};
    dimensionKeys.forEach((k, i) => (dimensionValues[k] = dimParts[i]));

    for (const date of allDates) {
      const existingRow = rows.find(r => r[dateKey] === date);

      const newRow: any = {};

      // 1. Add measure fields
      for (const measure of measureKeys) {
        if (existingRow && existingRow[measure] !== undefined) {
          newRow[measure] = existingRow[measure];
        } else {
          // Seeded unique value between 10–99
          const seed = `${dimKey}-${date}-${measure}`;
          const hash = Array.from(seed).reduce((acc, c) => acc + c.charCodeAt(0), 0);
          newRow[measure] = 10 + (hash % 90);
        }
      }

      // 2. Add date field
      newRow[dateKey] = date;

      // 3. Add dimension fields
      for (const k of dimensionKeys) {
        newRow[k] = dimensionValues[k];
      }

      result.push(newRow);
    }
  }

  return result;
}
const sampleData = [
    {
        "PROJECT_TYPE_COUNT": 12,
        "START_DATE": "2024-09-27",
        "MANAGER": "finally extricate"
    },
    {
        "PROJECT_TYPE_COUNT": 87,
        "START_DATE": "2024-10-02",
        "MANAGER": "finally extricate"
    },
    {
        "PROJECT_TYPE_COUNT": 33,
        "START_DATE": "2024-11-10",
        "MANAGER": "finally extricate"
    },
    {
        "PROJECT_TYPE_COUNT": 95,
        "START_DATE": "2024-11-18",
        "MANAGER": "finally extricate"
    },
    {
        "PROJECT_TYPE_COUNT": 92,
        "START_DATE": "2024-12-23",
        "MANAGER": "finally extricate"
    },
    {
        "PROJECT_TYPE_COUNT": 94,
        "START_DATE": "2025-02-07",
        "MANAGER": "finally extricate"
    },
    {
        "PROJECT_TYPE_COUNT": 90,
        "START_DATE": "2025-04-01",
        "MANAGER": "finally extricate"
    },
    {
        "PROJECT_TYPE_COUNT": 91,
        "START_DATE": "2025-05-01",
        "MANAGER": "finally extricate"
    },
    {
        "PROJECT_TYPE_COUNT": 94,
        "START_DATE": "2025-05-04",
        "MANAGER": "finally extricate"
    },
    {
        "PROJECT_TYPE_COUNT": 10,
        "START_DATE": "2025-06-09",
        "MANAGER": "finally extricate"
    },
   
]